import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { APPLICATION_API_BASE_URL } from '../../services/api/configuration/config';
import {
  AlertTriangle,
  CalendarClock,
  ChevronDown,
  ChevronUp,
  Copy,
  Flame,
  Mail,
  MessageCircle,
  PhoneForwarded,
  RefreshCw,
  Sparkles
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  draftAiEmailMessage,
  draftAiWhatsAppMessage,
  getAiLeadInsight,
  getAiLeadWhatsappTemplates,
  getStageQualificationProfile,
  refreshAiLeadInsight,
  scheduleAiOutboundMessage,
  sendAiEmailMessage,
  sendAiWhatsAppMessage,
  translateAiLeadContent
} from '../../services/api/implementation/impl';
import AiWhatsAppDrawer from './AiWhatsAppDrawer';
import AiScheduleSendDrawer from './AiScheduleSendDrawer';
import AiEmailDrawer from './AiEmailDrawer';
import AiFollowUpFromInsightModal from './AiFollowUpFromInsightModal';
import { languageDisplay } from './AiLanguageSelect';
import { useAiTranslationLanguages } from './useAiTranslationLanguages';
import {
  AiCardShell,
  AiInsightError,
  AiInsightSkeleton,
  AiCreditsNotice,
  AiSourceBadge,
  formatAiApiError,
  formatAiCreditBalance
} from './AiUiShared';
import AiCreditsRechargeAction from './AiCreditsRechargeAction';
import LeadIntelligencePanel from './LeadIntelligencePanel';
import StageQualificationProfilePanel from '../lead/StageQualificationProfilePanel';

const CACHE_STALE_MS = 15 * 60 * 1000;

function humanizeInsightToken(value) {
  if (!value || String(value).toLowerCase() === 'unknown') return null;
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function shouldOfferRegenerate(insight) {
  return Boolean(insight);
}

function refreshDisabledReason(insight) {
  if (!insight) return null;
  if (insight?.aiCredits?.depleted) {
    return 'AI credits are used up. Request a recharge or add your own API key under Integrations → AI Integration.';
  }
  if (insight?.aiCredits?.enforcementEnabled && insight?.aiCredits?.creditBalance <= 0) {
    return 'No AI credits remaining for this builder.';
  }
  return null;
}

function scoreChipTone(score) {
  if (score == null) return 'bg-slate-100 text-slate-700 ring-slate-200';
  if (score >= 70) return 'bg-emerald-50 text-emerald-800 ring-emerald-200';
  if (score >= 40) return 'bg-amber-50 text-amber-800 ring-amber-200';
  return 'bg-rose-50 text-rose-800 ring-rose-200';
}

function insightMetricTone(label) {
  if (label === 'Intent') return 'bg-blue-50 text-blue-800 ring-blue-100';
  if (label === 'Sentiment') return 'bg-slate-100 text-slate-700 ring-slate-200';
  if (label === 'Sales process') return 'bg-amber-50 text-amber-900 ring-amber-200';
  return 'bg-teal-50 text-[#1E4A53] ring-teal-100';
}

const TONE_OPTIONS = [
  { id: 'friendly', label: 'Friendly' },
  { id: 'formal', label: 'Formal' },
  { id: 'warm', label: 'Warm' },
  { id: 'urgent', label: 'Urgent' }
];

const TEMPLATE_OPTIONS = [
  { id: 'follow_up', label: 'Follow Up' },
  { id: 'general', label: 'General' },
  { id: 'site_visit', label: 'Site Visit' },
  { id: 'invoice_reminder', label: 'Invoice Reminder' }
];

const TEMPLATE_LABELS = Object.fromEntries(
  TEMPLATE_OPTIONS.map((o) => [o.id, o.label])
);

export default function AiInsightsCard({
  leadId,
  onSent,
  currentStage = 'Approached',
  hasActiveFollowUp = false,
  followUpId = null,
  followUpModalOpen: controlledFollowUpOpen,
  onFollowUpModalChange
}) {
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scheduleDrawerOpen, setScheduleDrawerOpen] = useState(false);
  const [scheduleChannel, setScheduleChannel] = useState('email');
  const [emailDrawerOpen, setEmailDrawerOpen] = useState(false);
  const [internalFollowUpOpen, setInternalFollowUpOpen] = useState(false);
  const followUpModalOpen = controlledFollowUpOpen ?? internalFollowUpOpen;
  const setFollowUpModalOpen = onFollowUpModalChange ?? setInternalFollowUpOpen;

  const [tone, setTone] = useState('friendly');
  const [templateKey, setTemplateKey] = useState('follow_up');
  const [draftMessage, setDraftMessage] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [draftTemplateKey, setDraftTemplateKey] = useState(null);
  const [templateKeyMismatchWarning, setTemplateKeyMismatchWarning] = useState(null);

  const [waTargetLanguage, setWaTargetLanguage] = useState('en');
  const [waEnglishMessage, setWaEnglishMessage] = useState('');
  const [waTranslationId, setWaTranslationId] = useState(null);

  const { languages: translationLanguages } = useAiTranslationLanguages();
  const hasTranslationTargets = translationLanguages.some((lang) => lang.code !== 'en');

  useEffect(() => {
    if (!translationLanguages.length) return;
    const codes = new Set(translationLanguages.map((lang) => lang.code));
    if (!codes.has(waTargetLanguage)) {
      setWaTargetLanguage('en');
      setWaTranslationId(null);
      if (waEnglishMessage) setDraftMessage(waEnglishMessage);
    }
  }, [translationLanguages, waTargetLanguage, waEnglishMessage]);

  const {
    data: insight,
    isLoading,
    isError,
    error,
    refetch,
    isFetching
  } = useQuery({
    queryKey: ['aiLeadInsight', leadId],
    queryFn: () => getAiLeadInsight(leadId),
    enabled: !!leadId,
    staleTime: CACHE_STALE_MS
  });

  const { data: waTemplateInfo } = useQuery({
    queryKey: ['aiLeadWhatsappTemplates', leadId],
    queryFn: () => getAiLeadWhatsappTemplates(leadId),
    enabled: !!leadId,
    staleTime: CACHE_STALE_MS
  });

  const { data: qualificationProfileData } = useQuery({
    queryKey: ['stageQualificationProfile', leadId],
    queryFn: () => getStageQualificationProfile(leadId),
    enabled: !!leadId,
    staleTime: 30_000
  });

  const qualificationProfile = qualificationProfileData?.cards ?? [];
  const qualificationSections = qualificationProfileData?.sections ?? [];
  const hasQualificationProfile =
    qualificationProfile.length > 0 || qualificationSections.length > 0;

  useEffect(() => {
    if (!waTemplateInfo) return;
    const templates = Array.isArray(waTemplateInfo.approvedTemplates)
      ? waTemplateInfo.approvedTemplates
      : [];
    setApprovedPinggoTemplates(templates);
    if (waTemplateInfo.defaultTemplateName) {
      setSelectedMetaTemplateName(waTemplateInfo.defaultTemplateName);
    } else if (templates[0]?.templateName) {
      setSelectedMetaTemplateName(templates[0].templateName);
    }
    setCanSendAsTemplate(!!waTemplateInfo.canSendAsTemplate);
    setPendingTemplateNote(waTemplateInfo.pendingTemplateNote || null);
    setSessionHint(waTemplateInfo.sessionHint || null);
    if (waTemplateInfo.sessionOpen === false) {
      setSessionOpen(false);
    }
  }, [waTemplateInfo]);

  const invalidateLeadInsight = () => {
    if (!leadId) return;
    queryClient.invalidateQueries({ queryKey: ['aiLeadInsight', leadId] });
    queryClient.invalidateQueries({ queryKey: ['aiLeadWhatsappTemplates', leadId] });
    queryClient.invalidateQueries({ queryKey: ['stageQualificationProfile', leadId] });
  };

  const handleActionComplete = () => {
    invalidateLeadInsight();
    onSent?.();
  };

  const refreshMutation = useMutation({
    mutationFn: () => refreshAiLeadInsight(leadId),
    onSuccess: () => {
      refetch();
      toast.success('AI insights regenerated');
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'Failed to regenerate insights');
    }
  });

  const [draftPhone, setDraftPhone] = useState('');
  const [draftRecipientName, setDraftRecipientName] = useState('');
  const [draftRecipientEmail, setDraftRecipientEmail] = useState('');
  const [sessionOpen, setSessionOpen] = useState(true);
  const [sessionBlockReason, setSessionBlockReason] = useState(null);
  const [sessionUserMessage, setSessionUserMessage] = useState(null);
  const [sessionHint, setSessionHint] = useState(null);
  const [pendingTemplateNote, setPendingTemplateNote] = useState(null);
  const [canSendAsTemplate, setCanSendAsTemplate] = useState(false);
  const [approvedPinggoTemplates, setApprovedPinggoTemplates] = useState([]);
  const [selectedMetaTemplateName, setSelectedMetaTemplateName] = useState('');
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [warningsExpanded, setWarningsExpanded] = useState(false);

  const applyWhatsAppDraft = (data) => {
    const english = data?.message || '';
    setWaEnglishMessage(english);
    setWaTargetLanguage('en');
    setWaTranslationId(null);
    setDraftMessage(english);
    setDraftPhone(data?.recipientPhone || '');
    setDraftRecipientName(data?.recipientName || insight?.leadName || '');
    if (data?.templateKey) setTemplateKey(data.templateKey);
    setDraftTemplateKey(data?.templateKey || templateKey);
    setTemplateKeyMismatchWarning(data?.templateKeyMismatchWarning || null);
    setSessionOpen(data?.sessionOpen !== false);
    setSessionBlockReason(data?.blockReason || null);
    setSessionUserMessage(data?.userMessage || null);
    setSessionHint(data?.sessionHint || null);
    setPendingTemplateNote(data?.pendingTemplateNote || null);
    setCanSendAsTemplate(!!data?.canSendAsTemplate);
    const templates = Array.isArray(data?.approvedTemplates) ? data.approvedTemplates : [];
    setApprovedPinggoTemplates(templates);
    const defaultName =
      data?.metaTemplateNameForKey ||
      data?.defaultTemplateName ||
      data?.reEngagementTemplateName ||
      templates[0]?.templateName ||
      '';
    setSelectedMetaTemplateName(defaultName);
  };

  const draftMutation = useMutation({
    mutationFn: ({ regenerate = false, templateKey: key } = {}) =>
      draftAiWhatsAppMessage(leadId, {
        tone,
        templateKey: key ?? templateKey,
        regenerate
      }),
    onSuccess: (data) => {
      applyWhatsAppDraft(data);
      setDrawerOpen(true);
      if (data?.templateKeyMismatchWarning) {
        toast(data.templateKeyMismatchWarning, { icon: '⚠️' });
      }
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'Failed to draft WhatsApp message');
    }
  });

  const draftMutationRef = useRef(draftMutation);
  draftMutationRef.current = draftMutation;

  const insightContextRef = useRef(null);
  useEffect(() => {
    if (!insight || isFetching) return;

    const signature = [
      insight.score,
      insight.funnelPhaseLabel,
      insight.recommendedTemplateKey,
      insight.nurtureRecommendation?.recommended
    ].join('|');
    const prev = insightContextRef.current;
    insightContextRef.current = signature;

    if (insight.recommendedTemplateKey && insight.recommendedTemplateKey !== templateKey) {
      setTemplateKey(insight.recommendedTemplateKey);
    }

    if (prev != null && prev !== signature) {
      setDraftMessage('');
      setWaEnglishMessage('');
      setWaTranslationId(null);
      setDraftTemplateKey(null);
      setTemplateKeyMismatchWarning(null);
      if (drawerOpen && !draftMutationRef.current.isPending) {
        const key = insight.recommendedTemplateKey || templateKey;
        draftMutationRef.current.mutate({ regenerate: false, templateKey: key });
      }
    }
  }, [insight, isFetching, drawerOpen, templateKey]);

  // Latest-ref so the SSE listener (subscribed once per lead) always runs the
  // current handler without re-subscribing on every render.
  const handleInboundReplyRef = useRef(() => {});
  handleInboundReplyRef.current = () => {
    if (!leadId) return;
    // Refresh insight + session/template status so recommendation, language,
    // and "last customer reply" reflect the new inbound immediately.
    queryClient.invalidateQueries({ queryKey: ['aiLeadInsight', leadId] });
    queryClient.invalidateQueries({ queryKey: ['aiLeadWhatsappTemplates', leadId] });
    // If the WhatsApp drawer is open, re-draft so the message resonates with the
    // reply (backend auto-invokes the LLM on a fresh unanswered inbound).
    if (drawerOpen && !draftMutation.isPending) {
      draftMutation.mutate({ regenerate: false, templateKey });
      toast('Customer just replied — draft updated', { icon: '💬' });
    }
  };

  useEffect(() => {
    if (!leadId) return undefined;
    const token = Cookies.get('token');
    if (!token) return undefined;

    const url = `${APPLICATION_API_BASE_URL}/whatsapp/stream?leadId=${encodeURIComponent(
      leadId
    )}&token=${encodeURIComponent(token)}`;
    const source = new EventSource(url);

    source.addEventListener('wa_message', (evt) => {
      let msg;
      try {
        msg = JSON.parse(evt.data);
      } catch {
        return;
      }
      // Only react to the customer's inbound replies.
      if (msg?.direction && String(msg.direction).toLowerCase() !== 'incoming') {
        return;
      }
      handleInboundReplyRef.current?.();
    });

    source.onerror = () => {
      // EventSource auto-reconnects; the query staleTime/refetch covers any gap.
    };

    return () => {
      source.close();
    };
  }, [leadId]);

  const scheduleEmailDraftMutation = useMutation({
    mutationFn: ({ regenerate = false, templateKey: key } = {}) =>
      draftAiEmailMessage(leadId, {
        tone,
        templateKey: key ?? templateKey,
        regenerate
      }),
    onSuccess: (data) => {
      setEmailSubject(data?.subject || '');
      setDraftMessage(data?.message || '');
      setDraftRecipientEmail(data?.recipientEmail || insight?.recipientEmail || '');
      setDraftRecipientName(data?.recipientName || insight?.leadName || '');
      setScheduleChannel('email');
      setScheduleDrawerOpen(true);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'Failed to prepare email');
    }
  });

  const emailDraftMutation = useMutation({
    mutationFn: ({ templateKey: key } = {}) =>
      draftAiEmailMessage(leadId, { tone, templateKey: key ?? templateKey }),
    onSuccess: (data) => {
      setEmailSubject(data?.subject || '');
      setDraftMessage(data?.message || '');
      setDraftRecipientEmail(data?.recipientEmail || insight?.recipientEmail || '');
      setDraftRecipientName(data?.recipientName || insight?.leadName || '');
      setEmailDrawerOpen(true);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'Failed to draft email');
    }
  });

  const handleOpenWhatsApp = () => {
    const key = insight?.recommendedTemplateKey || templateKey;
    if (key !== templateKey) setTemplateKey(key);
    draftMutation.mutate({ regenerate: false, templateKey: key });
  };

  const handleOpenEmail = () => {
    const key = insight?.recommendedTemplateKey || templateKey;
    if (key !== templateKey) setTemplateKey(key);
    emailDraftMutation.mutate({ templateKey: key });
  };

  const handleTemplateChange = (nextKey) => {
    setTemplateKey(nextKey);
    if (nextKey !== draftTemplateKey && draftTemplateKey) {
      setTemplateKeyMismatchWarning(
        `Draft was generated for "${TEMPLATE_LABELS[draftTemplateKey] || draftTemplateKey}". Regenerate after changing template.`
      );
    } else if (nextKey !== recommendedTemplateKey) {
      setTemplateKeyMismatchWarning(
        nextKey === 'invoice_reminder' && !insight?.warnings?.some((w) => w.code === 'DEAL_DUE')
          ? 'Invoice reminder is usually for booked customers with payment due.'
          : `Recommended for this lead: ${TEMPLATE_LABELS[recommendedTemplateKey] || recommendedTemplateKey}.`
      );
    } else {
      setTemplateKeyMismatchWarning(null);
    }
  };

  const translateWaMutation = useMutation({
    mutationFn: () =>
      translateAiLeadContent(leadId, {
        text: waEnglishMessage || draftMessage,
        targetLanguage: waTargetLanguage,
        contentType: 'whatsapp_draft'
      }),
    onSuccess: (data) => {
      if (!waEnglishMessage?.trim()) {
        setWaEnglishMessage(data?.sourceText || draftMessage);
      }
      setDraftMessage(data?.translatedText || '');
      setWaTranslationId(data?.translationId || null);
      toast.success(`Translated to ${languageDisplay(waTargetLanguage)}`);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'Translation failed');
    }
  });

  const handleWaLanguageChange = (code) => {
    setWaTargetLanguage(code);
    if (code === 'en') {
      setDraftMessage(waEnglishMessage || draftMessage);
      setWaTranslationId(null);
    }
  };

  const buildSendPayload = (base) => ({
    ...base,
    ...(waTargetLanguage !== 'en' && waEnglishMessage
      ? {
          originalMessage: waEnglishMessage,
          targetLanguage: waTargetLanguage,
          translationId: waTranslationId
        }
      : {})
  });

  const sendMutation = useMutation({
    mutationFn: ({ message, sendMode = 'text', metaTemplateName }) =>
      sendAiWhatsAppMessage(
        leadId,
        buildSendPayload({
          message,
          tone,
          templateKey,
          draftTemplateKey,
          sendMode,
          metaTemplateName
        })
      ),
    onSuccess: (data) => {
      const modeLabel = data?.sendMode === 'template' ? 'template' : 'message';
      if (data?.templateKeyMismatchWarning) {
        toast(data.templateKeyMismatchWarning, { icon: '⚠️' });
      }
      toast.success(`WhatsApp ${modeLabel} sent and logged to Sales Log`);
      // Keep the drawer open after sending so the rep can review/continue the
      // conversation; they close it manually.
      handleActionComplete();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'Failed to send WhatsApp');
    }
  });

  const sendEmailMutation = useMutation({
    mutationFn: () =>
      sendAiEmailMessage(leadId, {
        subject: emailSubject,
        message: draftMessage
      }),
    onSuccess: () => {
      toast.success('Email sent and logged to Sales Log');
      setEmailDrawerOpen(false);
      handleActionComplete();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'Failed to send email');
    }
  });

  const scheduleMutation = useMutation({
    mutationFn: (payload) =>
      scheduleAiOutboundMessage(
        leadId,
        buildSendPayload({
          channel: scheduleChannel,
          message: draftMessage,
          subject: scheduleChannel === 'email' ? emailSubject : undefined,
          scheduledAt: payload.scheduledAt,
          tone,
          templateKey,
          draftTemplateKey,
          sendMode: payload.sendMode,
          metaTemplateName: payload.metaTemplateName
        })
      ),
    onSuccess: () => {
      toast.success(
        scheduleChannel === 'email'
          ? 'Email scheduled successfully'
          : 'WhatsApp message scheduled successfully'
      );
      setScheduleDrawerOpen(false);
      setDrawerOpen(false);
      setEmailDrawerOpen(false);
      handleActionComplete();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'Failed to schedule message');
    }
  });

  const hotBadge = useMemo(
    () => insight?.badges?.find((b) => b.type === 'hot' || b.label?.includes('Hot')),
    [insight]
  );

  useEffect(() => {
    if (insight?.recommendedTemplateKey) {
      setTemplateKey(insight.recommendedTemplateKey);
    }
  }, [insight?.recommendedTemplateKey]);

  const recommendedTemplateKey = insight?.recommendedTemplateKey || 'follow_up';
  const scheduleSuggestion = insight?.scheduleSuggestion;
  const intelligence = insight?.intelligence;
  const warningList = insight?.warnings ?? [];

  const alertItems = useMemo(() => {
    const items = [];
    if (scheduleSuggestion?.reason) {
      items.push({
        message: scheduleSuggestion.reason,
        severity: scheduleSuggestion.overdue ? 'warning' : 'info'
      });
    }
    warningList.forEach((w) => {
      items.push({ message: w.message, severity: 'warning' });
    });
    return items;
  }, [scheduleSuggestion, warningList]);

  const metricChips = useMemo(() => {
    if (!insight) return [];
    return [
      { label: 'Intent', value: humanizeInsightToken(insight.intentLevel) },
      { label: 'Sentiment', value: humanizeInsightToken(insight.sentiment) },
      { label: 'Category', value: humanizeInsightToken(insight.aiCategory) },
      { label: 'Urgency', value: humanizeInsightToken(insight.urgencyTag) }
    ].filter((chip) => chip.value);
  }, [insight]);

  const profileMetricChips = useMemo(() => {
    if (!insight) return [];
    const chips = [];
    const intent = humanizeInsightToken(insight.intentLevel);
    const sentiment = humanizeInsightToken(insight.sentiment);
    if (intent) chips.push({ label: 'Intent', value: intent });
    if (sentiment) chips.push({ label: 'Sentiment', value: sentiment });
    const salesDim =
      insight.dimensionSignals?.find(
        (d) =>
          d.code === 'SALES_FOLLOW_UP' ||
          d.code === 'SALES_PROCESS' ||
          String(d.label ?? '')
            .toLowerCase()
            .includes('sales process')
      ) ?? null;
    if (salesDim) {
      const processHundred = salesDim.detail?.match(/(\d+)\/100/)?.[1];
      chips.push({
        label: 'Sales process',
        value: processHundred ? `${processHundred}/100` : `${salesDim.score}/${salesDim.maxScore}`,
        title: salesDim.detail || undefined,
        compactLabel: true
      });
    }
    return chips;
  }, [insight]);

  const confidenceScore = useMemo(() => {
    if (hotBadge?.confidence != null) return hotBadge.confidence;
    if (insight?.score != null) return insight.score;
    return null;
  }, [hotBadge, insight]);

  const handleCopySummary = async () => {
    const text = [
      insight?.summary,
      insight?.nextBestActions?.length
        ? `Suggested next steps: ${insight.nextBestActions.map((a) => a.action).join('; ')}`
        : null
    ]
      .filter(Boolean)
      .join('\n\n');
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Could not copy');
    }
  };

  const handleWaMessageChange = (text) => {
    setDraftMessage(text);
    if (waTargetLanguage === 'en') {
      setWaEnglishMessage(text);
      setWaTranslationId(null);
    }
  };

  if (!leadId) return null;

  const isDrafting =
    draftMutation.isPending ||
    scheduleEmailDraftMutation.isPending ||
    emailDraftMutation.isPending;

  const requiresPinggoTemplate =
    waTemplateInfo?.requiresTemplate && waTemplateInfo?.canSendAsTemplate;
  const visibleAlerts = warningsExpanded ? alertItems : alertItems.slice(0, 2);
  const summaryText = insight?.summary || 'No summary available yet.';
  const summaryIsLong = summaryText.length > 220;

  const creditsMeta =
    insight?.aiCredits?.enforcementEnabled && !insight?.aiCredits?.depleted
      ? `${formatAiCreditBalance(insight.aiCredits)} AI credits`
      : null;

  const showRegenerate = shouldOfferRegenerate(insight);
  const refreshBlockedReason = refreshDisabledReason(insight);
  const isRegenerating = refreshMutation.isPending || isFetching;
  const activeMetricChips = hasQualificationProfile ? profileMetricChips : metricChips;

  return (
    <>
      <AiCardShell className="!from-white !via-white !to-teal-50/20 !border-teal-100 !p-0 overflow-hidden">
        <div
          className={`border-b border-teal-100/80 bg-gradient-to-r from-teal-50/40 via-white to-white ${
            hasQualificationProfile ? 'px-3 py-2' : 'px-4 py-3 sm:px-5'
          }`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-1.5 min-w-0">
              <Sparkles className="h-4 w-4 text-[#1E4A53] shrink-0" />
              <h3 className="text-base font-bold text-gray-900">AI Insights</h3>
              {!isLoading && !isError ? (
                <AiSourceBadge
                  source={insight?.source}
                  cacheStatus={insight?.cacheStatus}
                  llmInvoked={insight?.llmInvoked}
                />
              ) : null}
              {!isLoading && !isError && hotBadge && !hasQualificationProfile ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700 ring-1 ring-red-200">
                  <Flame className="h-3 w-3" />
                  {hotBadge.label}
                </span>
              ) : null}
            </div>
            {!isLoading && !isError && showRegenerate ? (
              <button
                type="button"
                onClick={() => refreshMutation.mutate()}
                disabled={isRegenerating || Boolean(refreshBlockedReason)}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-teal-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-[#1E4A53] shadow-sm hover:bg-teal-50 disabled:cursor-not-allowed disabled:opacity-50"
                title={
                  refreshBlockedReason ||
                  'Refresh AI summary for this lead (uses credits when AI polish runs)'
                }>
                <RefreshCw className={`h-3.5 w-3.5 ${isRegenerating ? 'animate-spin' : ''}`} />
                {insight?.source === 'ai' || insight?.llmInvoked ? 'Refresh summary' : 'Regenerate'}
              </button>
            ) : null}
          </div>

          {!hasQualificationProfile && !isLoading && !isError && creditsMeta ? (
            <p className="mt-1 text-xs text-gray-500">{creditsMeta}</p>
          ) : null}

          {!hasQualificationProfile ? (
            <div className="mt-1.5 space-y-1.5">
              <AiCreditsNotice aiCredits={insight?.aiCredits} />
              <AiCreditsRechargeAction aiCredits={insight?.aiCredits} compact />
            </div>
          ) : insight?.aiCredits?.depleted ? (
            <div className="mt-1">
              <AiCreditsNotice aiCredits={insight?.aiCredits} />
            </div>
          ) : null}
        </div>

        {isLoading ? (
          <div className="p-5">
            <AiInsightSkeleton lines={3} />
          </div>
        ) : isError ? (
          <div className="p-5">
            <AiInsightError message={formatAiApiError(error)} onRetry={() => refetch()} />
          </div>
        ) : (
          <div
            className={`grid grid-cols-1 ${
              hasQualificationProfile
                ? 'xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]'
                : 'xl:grid-cols-[minmax(0,1fr)_200px]'
            }`}>
            <div
              className={`space-y-2 xl:border-r xl:border-teal-100/80 ${
                hasQualificationProfile ? 'px-3 py-3' : 'px-4 py-4 sm:px-5'
              }`}>
              {!isLoading && !isError && confidenceScore != null ? (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  <div className="shrink-0">
                    <p className="text-2xl font-bold tabular-nums text-gray-900 leading-none">
                      {confidenceScore}
                      <span className="text-base font-bold text-gray-400">/100</span>
                    </p>
                    <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-gray-400">
                      Lead score
                    </p>
                  </div>
                  {activeMetricChips.length > 0 ? (
                    <div className="flex flex-wrap items-center gap-1.5 min-w-0 flex-1">
                      {activeMetricChips.map((chip) => (
                        <span
                          key={chip.label}
                          title={chip.title || undefined}
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${insightMetricTone(chip.label)}`}>
                          {chip.compactLabel ? (
                            <span>
                              {chip.label} {chip.value}
                            </span>
                          ) : (
                            <>
                              <span>{chip.label}:</span>
                              <span className="font-bold">{chip.value}</span>
                            </>
                          )}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : activeMetricChips.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {activeMetricChips.map((chip) => (
                    <span
                      key={chip.label}
                      title={chip.title || undefined}
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${insightMetricTone(chip.label)}`}>
                      {chip.compactLabel ? (
                        <span>
                          {chip.label} {chip.value}
                        </span>
                      ) : (
                        <>
                          <span>{chip.label}:</span>
                          <span className="font-bold">{chip.value}</span>
                        </>
                      )}
                    </span>
                  ))}
                </div>
              ) : null}

              <div>
                <p
                  className={`leading-snug text-gray-800 text-sm font-medium ${
                    !summaryExpanded && summaryIsLong ? 'line-clamp-3' : ''
                  }`}>
                  {summaryText}
                </p>
                {summaryIsLong ? (
                  <button
                    type="button"
                    onClick={() => setSummaryExpanded((v) => !v)}
                    className="mt-1 inline-flex items-center gap-0.5 text-sm font-semibold text-[#1E4A53] hover:underline">
                    {summaryExpanded ? (
                      <>
                        Show less <ChevronUp className="h-3.5 w-3.5" />
                      </>
                    ) : (
                      <>
                        Read more <ChevronDown className="h-3.5 w-3.5" />
                      </>
                    )}
                  </button>
                ) : null}
              </div>

              {insight?.nextBestActions?.length > 0 ? (
                <div className="rounded-lg border border-sky-100 bg-sky-50/80 px-2.5 py-2">
                  <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-sky-900/90">
                    Suggested next steps
                  </p>
                  <ul className="space-y-1">
                    {insight.nextBestActions.slice(0, 3).map((action, idx) => (
                      <li key={idx} className="flex gap-1.5 text-[13px] font-medium text-gray-800 leading-snug">
                        <span className="text-sky-700 shrink-0 font-bold">•</span>
                        <span>{action.action}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {requiresPinggoTemplate ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50/80 px-3 py-2.5">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-amber-900">
                    Pinggo template (first contact)
                  </p>
                  <p className="mt-1 text-[11px] leading-relaxed text-amber-950">
                    No recent WhatsApp reply — an approved template is required.
                  </p>
                  <select
                    value={selectedMetaTemplateName}
                    onChange={(e) => setSelectedMetaTemplateName(e.target.value)}
                    className="mt-2 w-full max-w-md rounded-lg border border-amber-200 bg-white px-2.5 py-1.5 text-xs">
                    {approvedPinggoTemplates.map((t) => (
                      <option key={t.templateName} value={t.templateName}>
                        {t.templateName.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {alertItems.length > 0 ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2 text-sm leading-relaxed text-amber-950 min-w-0">
                      {alertItems[0].severity === 'info' ? (
                        <CalendarClock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-600" />
                      ) : (
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                      )}
                      <span>{visibleAlerts[0]?.message}</span>
                    </div>
                    {alertItems.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => setWarningsExpanded((v) => !v)}
                        className="shrink-0 text-sm font-semibold text-[#1E4A53] hover:underline whitespace-nowrap">
                        {warningsExpanded
                          ? 'Show less'
                          : `Show ${alertItems.length - 1} more`}
                      </button>
                    ) : null}
                  </div>
                  {warningsExpanded && alertItems.length > 1 ? (
                    <ul className="mt-2 space-y-1.5 border-t border-amber-200/60 pt-2">
                      {alertItems.slice(1).map((alert, idx) => (
                        <li
                          key={idx}
                          className="flex items-start gap-2 text-sm leading-relaxed text-amber-950">
                          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                          <span>{alert.message}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}

              <LeadIntelligencePanel
                intelligence={intelligence}
                dimensionSignals={insight?.dimensionSignals}
              />
            </div>

            {hasQualificationProfile ? (
              <div className="flex flex-col border-t border-teal-100/80 bg-white xl:border-t-0 xl:border-l xl:border-teal-100/80 xl:self-stretch">
                <StageQualificationProfilePanel
                  embedded
                  leadId={leadId}
                  cards={qualificationProfile}
                  sections={qualificationSections}
                  onUpdated={(data) => {
                    queryClient.setQueryData(['stageQualificationProfile', leadId], data);
                    queryClient.invalidateQueries({ queryKey: ['stageQualificationProfile', leadId] });
                    queryClient.invalidateQueries({ queryKey: ['aiLeadInsight', leadId] });
                    queryClient.invalidateQueries({ queryKey: ['aiLeadWhatsappTemplates', leadId] });
                    onSent?.();
                  }}
                />
                <div className="mt-auto grid grid-cols-2 gap-1.5 border-t border-gray-100 p-3">
                    <button
                      type="button"
                      onClick={handleOpenWhatsApp}
                      disabled={isDrafting}
                      className="col-span-2 inline-flex items-center justify-center gap-2 rounded-lg bg-[#25D366] px-3 py-2 text-sm font-bold text-white hover:bg-[#1fb855] disabled:opacity-60">
                      <MessageCircle className="h-4 w-4 shrink-0" />
                      {isDrafting ? 'Preparing…' : 'WhatsApp'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setFollowUpModalOpen(true)}
                      className="inline-flex items-center justify-center gap-1 rounded-lg border border-[#1E4A53]/25 bg-white px-2 py-2 text-xs font-bold text-[#1E4A53] hover:bg-teal-50">
                      <PhoneForwarded className="h-3.5 w-3.5" />
                      Follow up
                    </button>
                    <button
                      type="button"
                      onClick={handleOpenEmail}
                      disabled={emailDraftMutation.isPending}
                      className="inline-flex items-center justify-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-2 text-xs font-bold text-gray-800 hover:bg-gray-50 disabled:opacity-60">
                      <Mail className="h-3.5 w-3.5 text-[#1E4A53]" />
                      Email
                    </button>
                    <button
                      type="button"
                      onClick={handleCopySummary}
                      className="col-span-2 inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50 hover:text-[#1E4A53]">
                      <Copy className="h-3.5 w-3.5" />
                      Copy summary
                    </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-row flex-wrap gap-2 border-t border-teal-100/80 bg-teal-50/20 p-4 xl:flex-col xl:border-t-0 xl:sticky xl:top-0 xl:self-start">
                <p className="w-full text-xs font-bold uppercase tracking-wider text-gray-500">Actions</p>
                <button
                  type="button"
                  onClick={handleOpenWhatsApp}
                  disabled={isDrafting}
                  className="inline-flex flex-1 min-w-[7rem] items-center justify-center gap-2 rounded-xl bg-[#25D366] px-3 py-2.5 text-sm font-semibold text-white hover:bg-[#1fb855] disabled:opacity-60 xl:w-full xl:flex-none">
                  <MessageCircle className="h-4 w-4 shrink-0" />
                  {isDrafting ? 'Preparing…' : 'WhatsApp'}
                </button>
                <button
                  type="button"
                  onClick={() => setFollowUpModalOpen(true)}
                  className="inline-flex flex-1 min-w-[7rem] items-center justify-center gap-2 rounded-xl border border-[#1E4A53]/25 bg-white px-3 py-2.5 text-sm font-semibold text-[#1E4A53] hover:bg-teal-50 xl:w-full xl:flex-none">
                  <PhoneForwarded className="h-4 w-4" />
                  Follow up
                </button>
                <button
                  type="button"
                  onClick={handleOpenEmail}
                  disabled={emailDraftMutation.isPending}
                  className="inline-flex flex-1 min-w-[7rem] items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-60 xl:w-full xl:flex-none">
                  <Mail className="h-4 w-4 text-[#1E4A53]" />
                  Email
                </button>
                <button
                  type="button"
                  onClick={handleCopySummary}
                  className="inline-flex flex-1 min-w-[7rem] items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-[#1E4A53] xl:w-full xl:flex-none">
                  <Copy className="h-3.5 w-3.5" />
                  Copy summary
                </button>
              </div>
            )}
          </div>
        )}
      </AiCardShell>

      <AiWhatsAppDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        recipientName={draftRecipientName || insight?.leadName}
        recipientPhone={draftPhone || insight?.recipientPhone}
        tone={tone}
        templateKey={templateKey}
        recommendedTemplateKey={recommendedTemplateKey}
        templateKeyMismatchWarning={templateKeyMismatchWarning}
        toneOptions={TONE_OPTIONS}
        templateOptions={TEMPLATE_OPTIONS}
        message={draftMessage}
        sessionOpen={sessionOpen}
        sessionBlockReason={sessionBlockReason}
        sessionUserMessage={sessionUserMessage}
        sessionHint={sessionHint}
        pendingTemplateNote={pendingTemplateNote}
        canSendAsTemplate={canSendAsTemplate}
        approvedPinggoTemplates={approvedPinggoTemplates}
        selectedMetaTemplateName={selectedMetaTemplateName}
        onMetaTemplateChange={setSelectedMetaTemplateName}
        lastCustomerReplyAt={draftMutation.data?.lastCustomerReplyAt}
        hoursSinceCustomerReply={draftMutation.data?.hoursSinceCustomerReply}
        scheduleSuggestion={scheduleSuggestion}
        onToneChange={setTone}
        onTemplateChange={handleTemplateChange}
        onMessageChange={handleWaMessageChange}
        targetLanguage={waTargetLanguage}
        onTargetLanguageChange={handleWaLanguageChange}
        translationLanguages={translationLanguages}
        showTranslation={hasTranslationTargets}
        originalEnglishMessage={waEnglishMessage}
        onTranslate={() => translateWaMutation.mutate()}
        isTranslating={translateWaMutation.isPending}
        onRegenerate={() => draftMutation.mutate({ regenerate: true, templateKey })}
        onSend={() => sendMutation.mutate({ message: draftMessage, sendMode: 'text' })}
        onSendTemplate={() =>
          sendMutation.mutate({
            message: draftMessage,
            sendMode: 'template',
            metaTemplateName: selectedMetaTemplateName
          })
        }
        onSchedule={(payload) => {
          setScheduleChannel('whatsapp');
          scheduleMutation.mutate(payload);
        }}
        isRegenerating={draftMutation.isPending}
        isSending={sendMutation.isPending}
        isScheduling={scheduleMutation.isPending}
      />

      <AiScheduleSendDrawer
        open={scheduleDrawerOpen}
        onClose={() => setScheduleDrawerOpen(false)}
        channel="email"
        recipientName={draftRecipientName || insight?.leadName}
        recipientLabel={draftRecipientEmail || insight?.recipientEmail}
        scheduleSuggestion={scheduleSuggestion}
        tone={tone}
        templateKey={templateKey}
        templateOptions={TEMPLATE_OPTIONS}
        toneOptions={TONE_OPTIONS}
        message={draftMessage}
        subject={emailSubject}
        onToneChange={setTone}
        onTemplateChange={handleTemplateChange}
        onSubjectChange={setEmailSubject}
        onMessageChange={setDraftMessage}
        onRegenerate={() =>
          scheduleEmailDraftMutation.mutate({
            regenerate: true,
            templateKey
          })
        }
        onSchedule={(payload) => scheduleMutation.mutate(payload)}
        isRegenerating={scheduleEmailDraftMutation.isPending}
        isScheduling={scheduleMutation.isPending}
      />

      <AiEmailDrawer
        open={emailDrawerOpen}
        onClose={() => setEmailDrawerOpen(false)}
        recipientName={draftRecipientName || insight?.leadName}
        recipientEmail={draftRecipientEmail || insight?.recipientEmail}
        subject={emailSubject}
        message={draftMessage}
        templateKey={templateKey}
        templateOptions={TEMPLATE_OPTIONS}
        tone={tone}
        toneOptions={TONE_OPTIONS}
        onToneChange={setTone}
        onTemplateChange={(key) => {
          handleTemplateChange(key);
          emailDraftMutation.mutate({ templateKey: key });
        }}
        onSubjectChange={setEmailSubject}
        onMessageChange={setDraftMessage}
        onRegenerate={() => emailDraftMutation.mutate({ templateKey })}
        onSendNow={() => sendEmailMutation.mutate()}
        onSchedule={() => {
          setScheduleChannel('email');
          setEmailDrawerOpen(false);
          setScheduleDrawerOpen(true);
        }}
        isRegenerating={emailDraftMutation.isPending}
        isSending={sendEmailMutation.isPending}
      />

      <AiFollowUpFromInsightModal
        open={followUpModalOpen}
        onClose={() => setFollowUpModalOpen(false)}
        leadId={leadId}
        scheduleSuggestion={scheduleSuggestion}
        currentStage={currentStage}
        hasActiveFollowUp={hasActiveFollowUp}
        followUpId={followUpId}
        onSuccess={handleActionComplete}
      />
    </>
  );
}
